import { estFiltreListe, type FiltreListe } from '@rh/shared';
import { TRIS, type Tri } from './tri-liste';

/**
 * Filtres de la liste par annonce gardés le temps de la session (demande du
 * 2026-09-19 : « garder le filtre tant que la session n'a pas été clôturée ou
 * le filtre annulé »). Ouvrir une fiche puis revenir à la liste, ou passer
 * d'une annonce à l'autre et revenir, retrouve la même sélection.
 *
 * `sessionStorage` : la mémoire meurt avec l'onglet, et `oublierFiltres()`
 * l'efface à la déconnexion. Rien de personnel n'y entre — un filtre rapide,
 * un tri, et les critères tels qu'ils s'écrivent déjà dans l'adresse. Tout
 * accès est protégé : navigation privée ou stockage refusé, l'écran marche
 * sans mémoire plutôt que de tomber en panne.
 */
const CLE = 'rh-app:filtres-liste';

export interface FiltresGardes {
  filtre: FiltreListe;
  /** Critères combinables sous la forme qu'ils ont dans l'adresse (`site=…&cv=…`). */
  criteres: string;
  tri: Tri;
}

export const FILTRES_PAR_DEFAUT: FiltresGardes = { filtre: 'toutes', criteres: '', tri: 'defaut' };

function estParDefaut(etat: FiltresGardes): boolean {
  return etat.filtre === 'toutes' && etat.criteres === '' && etat.tri === 'defaut';
}

function lireTout(): Record<string, FiltresGardes> {
  try {
    const brut = sessionStorage.getItem(CLE);
    if (!brut) return {};
    const lu: unknown = JSON.parse(brut);
    return lu && typeof lu === 'object' && !Array.isArray(lu) ? (lu as Record<string, FiltresGardes>) : {};
  } catch {
    return {};
  }
}

function ecrireTout(tout: Record<string, FiltresGardes>): void {
  try {
    if (Object.keys(tout).length === 0) sessionStorage.removeItem(CLE);
    else sessionStorage.setItem(CLE, JSON.stringify(tout));
  } catch {
    // Stockage refusé (navigation privée, quota) : on se passe de mémoire.
  }
}

/**
 * Filtres gardés pour cette annonce, ou `null`. Une valeur devenue inconnue
 * (version précédente, mémoire bricolée) est ramenée à son défaut plutôt que
 * de faire échouer l'écran.
 */
export function lireFiltres(annonceId: string): FiltresGardes | null {
  const garde = lireTout()[annonceId] as Partial<FiltresGardes> | undefined;
  if (!garde) return null;
  return {
    filtre: estFiltreListe(garde.filtre) ? garde.filtre : 'toutes',
    criteres: typeof garde.criteres === 'string' ? garde.criteres.slice(0, 2000) : '',
    tri: (TRIS as readonly string[]).includes(garde.tri ?? '') ? (garde.tri as Tri) : 'defaut',
  };
}

/** Garde les filtres de cette annonce ; revenus au défaut, ils sont oubliés. */
export function ecrireFiltres(annonceId: string, etat: FiltresGardes): void {
  const tout = lireTout();
  if (estParDefaut(etat)) delete tout[annonceId];
  else tout[annonceId] = etat;
  ecrireTout(tout);
}

/** Fin de session : plus aucun filtre gardé. */
export function oublierFiltres(): void {
  try {
    sessionStorage.removeItem(CLE);
  } catch {
    // Rien à faire : sans stockage, il n'y avait rien à oublier.
  }
}
